import { ElMessage } from "element-plus";

import {
  getMockUrl,
  getPrefixUrl,
  mockJwtMap,
  checkMallFetchUrl,
  checkCollectionFetchUrl,
  getCurrentServer,
  getMockJwtByPrefixUrl,
  getMallApiBaseUrlFromStoreInfoMap,
  getMallAppToken,
  getToken,
  isDisabledMallSsr,
  isMallProxyRequest,
  rewriteMallProxyUrl,
  getCurrentSiteHost,
  setMallAppToken,
  setToken,
} from "../utils/globalData";
import { useMainStore } from "../composables/store";
import { alibabaProductTextTranslate } from "../api/mall";


import { get, isArray, isObject, isString ,set} from "lodash-es";

function getHeaderValue(headers, key) {
  if (!headers || !key) {
    return "";
  }
  if (typeof headers.get === "function") {
    return headers.get(key) || headers.get(String(key).toLowerCase()) || "";
  }
  return headers[key] || headers[String(key).toLowerCase()] || "";
}

const FORWARDED_REQUEST_HEADER_BLACKLIST = new Set([
  "connection",
  "content-length",
  "keep-alive",
  "proxy-authenticate",
  "proxy-authorization",
  "te",
  "trailer",
  "transfer-encoding",
  "upgrade",
]);

function appendHeaders(target = {}, sourceHeaders = {}, { filterHopByHop = false } = {}) {
  if (!sourceHeaders) {
    return target;
  }

  const setHeader = (rawKey, rawValue) => {
    const key = String(rawKey || "").trim();
    if (!key || rawValue === undefined || rawValue === null || rawValue === "") {
      return;
    }

    const normalizedKey = key.toLowerCase();
    if (filterHopByHop && FORWARDED_REQUEST_HEADER_BLACKLIST.has(normalizedKey)) {
      return;
    }

    target[key] = Array.isArray(rawValue) ? rawValue.join(", ") : String(rawValue);
  };

  if (typeof sourceHeaders.forEach === "function") {
    sourceHeaders.forEach((value, key) => {
      setHeader(key, value);
    });
    return target;
  }

  Object.entries(sourceHeaders).forEach(([key, value]) => {
    setHeader(key, value);
  });

  return target;
}

function buildForwardHeaders(requestHeaders = {}, optionHeaders = {}) {
  const mergedHeaders = {};

  if (process.server) {
    appendHeaders(mergedHeaders, requestHeaders, { filterHopByHop: true });
  }

  appendHeaders(mergedHeaders, optionHeaders);

  return mergedHeaders;
}

function findDeepValText(currentItem = {}, findKeys = [], transferList = []) {
  // 递归查找翻译的中文
  findKeys.map((keys) => {
    keys.map((key, keyIx) => {
      let selectItem = get(currentItem, key);
      if (isArray(selectItem)) {
        selectItem.map((o) => {
          findDeepValText(o, [keys.slice(keyIx + 1)], transferList);
        });
      } else if (isObject(selectItem)) {
        findDeepValText(selectItem,[ keys.slice(keyIx + 1)], transferList);
      } else if (isString(selectItem)) {
        transferList.push(selectItem);
      }
    });
  });
}

function resolveDirectMallRequest(url, store, headers) {
  // disabledMallSsr 开启后，客户端 mall 请求直接命中 otherJson.mallApiUrl，
  // 避免再经过 Nuxt mall 代理链。
  if (!process.client || !isDisabledMallSsr() || !isMallProxyRequest(url)) {
    return null;
  }

  const mallApiBaseUrl = getMallApiBaseUrlFromStoreInfoMap(
    store?.$state?.storeInfoDataMap,
    getCurrentServer(headers),
  );

  if (!mallApiBaseUrl) {
    return null;
  }

  return {
    requestUrl: rewriteMallProxyUrl(url),
    baseURL: mallApiBaseUrl,
  };
}

function removeMallJwtHeader(headers = {}) {
  if (!headers || typeof headers !== "object") {
    return headers;
  }

  // mall 侧接口不接受站点 openaccessjwt，这里统一在发送前剔除。
  if (typeof headers.delete === "function") {
    headers.delete("openaccessjwt");
    headers.delete("openAccessJwt");
  }

  Object.keys(headers).forEach((key) => {
    if (String(key || "").toLowerCase() === "openaccessjwt") {
      delete headers[key];
    }
  });
  return headers;
}

function deleteHeaderValue(headers = {}, key = "") {
  if (!headers || !key) {
    return;
  }

  if (typeof headers.delete === "function") {
    headers.delete(key);
    headers.delete(String(key).toLowerCase());
  }

  delete headers[key];
  delete headers[String(key).toLowerCase()];
}

export default defineNuxtPlugin((nuxtApp) => {
  const initialRequestHeaders = process.server ? useRequestHeaders() : {};

  const customFetch = async (url, options = {}, requestHeaders = initialRequestHeaders) => {
    const store = useMainStore();
    const headers = requestHeaders || initialRequestHeaders || {};
    const hostSite = getCurrentSiteHost(headers);

    const tokenKey = `openaccessjwt_${hostSite}`;

    // 设置默认请求头
    const defaultHeaders = buildForwardHeaders(headers, options.headers);

    // 请求方法和参数处理
    const directMallRequest = resolveDirectMallRequest(url, store, headers);
    const requestUrl = directMallRequest?.requestUrl || url;
    const isMallRequest = checkMallFetchUrl(requestUrl);
    const prefixUrl = getPrefixUrl(requestUrl, headers);

    // 只有站点/collection 请求才透传 openaccessjwt，mall 请求始终不带这个头。
    if (!isMallRequest && getMockJwtByPrefixUrl(prefixUrl)) {
      defaultHeaders["openaccessjwt"] = getMockJwtByPrefixUrl(prefixUrl);
    }

    if (!isMallRequest && store && store.$state.tokenKeyMap[tokenKey]) {
      defaultHeaders["openaccessjwt"] = store.$state.tokenKeyMap[tokenKey];
    }

    if (isMallRequest) {
      removeMallJwtHeader(defaultHeaders);
    }

    const requestLanguage = getHeaderValue(defaultHeaders, "language");
    const requestTransferKeysConfig = getHeaderValue(defaultHeaders, "transferKeysConfig");
    deleteHeaderValue(defaultHeaders, "language");
    deleteHeaderValue(defaultHeaders, "transferKeysConfig");

    let fetchOptions = {
      ...options,
      headers: defaultHeaders,
      baseURL: directMallRequest?.baseURL || prefixUrl,
    };

    if (process.client) {
      // 客户端登录信息管理
      let isMallUrl = isMallRequest;
      let isCollectionUrl = checkCollectionFetchUrl(requestUrl);
      if (isMallUrl) {
        fetchOptions.headers.authorization = getMallAppToken();
      } else if (isCollectionUrl) {
        const token = getToken();
        if (typeof fetchOptions.headers != "undefined") {
          if (typeof token === "string") {
            fetchOptions.headers.authorization = token;
          } else {
            fetchOptions.headers.authorization = "";
          }
        }
      }
    }

    try {
      // 发送请求
      const response = await $fetch.raw(requestUrl, fetchOptions);
      const resHeaders = response.headers;
      const responseAuthorization = getHeaderValue(resHeaders, "authorization");

      if (process.client && responseAuthorization) {
        // 先同步 token，保证首个 mall 请求也能走后续翻译注入逻辑
        let isMallUrl = checkMallFetchUrl(requestUrl);
        let isCollectionUrl = checkCollectionFetchUrl(requestUrl);
        if (isMallUrl) {
          setMallAppToken(responseAuthorization);
        } else if (isCollectionUrl) {
          setToken(responseAuthorization);
        }
      }

      try{
        const mallAuthorization =
          fetchOptions.headers?.authorization || responseAuthorization || getMallAppToken();
        if (process.client&&mallAuthorization&&response._data&&requestLanguage && requestTransferKeysConfig) {
          let transferKeysConfig = [];
          try {
            transferKeysConfig = JSON.parse(requestTransferKeysConfig);
          } catch (e) {}
          if (transferKeysConfig.length) {
            let transferList = [];
            findDeepValText(response._data,transferKeysConfig,transferList);
            transferList = Array.from(
              new Set(
                transferList
                  .map((item) => String(item || "").trim())
                  .filter(Boolean)
              )
            );
            if (!transferList.length) {
              return response._data;
            }
          
            let resTransferLang= await alibabaProductTextTranslate({
              "param":{
                "sourceTextList": transferList.join("\n"),
                "sourceLanguage":"zh",
                "targetLanguage":requestLanguage,
                "formatType":"text"
              }
            });
            let transfers=[]
            let transfersMap={}
            let transferRes = get(resTransferLang,'data.result');
            console.log("transferRes",transferRes)
            try{
              transfers=transferRes.split("\n")
            }catch(e){
              console.log(e)
            }

            transferList.map((k,i)=>{
              transfersMap[k]=transfers[i]
            })
            set(response._data,'data.transfersMap',transfersMap);
          
            
          }
        }
      }catch(e){
        console.log(e)
      }

      // 检查响应状态
      if (
        !response.ok ||
        (typeof response._data.success == "boolean" && !response._data.success)
      ) {
        throw new Error(
          `HTTP error! status: ${response.status},data:${JSON.stringify(
            response._data
          )}`
        );
      }

      //  const resHeaders ={}// useResponseHeader(['openaccessjwt','tracking_query_meta'])

      // 如果响应中有新的 token，更新 store
      if (resHeaders.has("openaccessjwt")) {
        store.setTokenKeyMap(tokenKey, resHeaders.get("openaccessjwt"));
      }

      // 如果有 tracking_query_meta，将其存储在 localStorage
      if (resHeaders.has("tracking_query_meta") && process.client) {
        window.localStorage.setItem(
          "tracking_query_meta",
          resHeaders.get("tracking_query_meta")
        );
      }

      // 返回响应数据
      return response._data;
    } catch (error) {
      // 错误处理
      console.error(error);
      const errorMessage = error.message || "请求失败";

      // 错误码处理
      if (error.message.includes("401")&&!requestUrl.match('uncheck401=1')) {
        if (process.client) {
          debugger
          // 客户端登录信息管理
          let isMallUrl = checkMallFetchUrl(requestUrl);
          let isCollectionUrl = checkCollectionFetchUrl(requestUrl);
          if ((isMallUrl || isCollectionUrl)&&!requestUrl.match('uncheck401=1')) {
            ElMessage({
              showClose: true,
              message: "请先登录",
              type: "error",
            });
            store.logout();
          } else {
            ElMessage({
              showClose: true,
              message: errorMessage,
              type: "error",
            });
          }
        }
      } else if (error.message.includes("403")) {
        if (process.server) {
          navigateTo("/error/forbidden");
        }
      } else if (error.message.includes("404")) {
        if (process.server) {
          navigateTo("/error/not-found");
        }
      } else if (error.message.includes("500")) {
        if (process.client) {
          ElMessage({
            showClose: true,
            message: errorMessage,
            type: "error",
          });
        }
      }

      throw error;
    }
  };

  // 将 customFetch 提供给 Nuxt 应用
  nuxtApp.provide("customFetch", customFetch);
});
